// ─── "מה אם" · the portfolio before and after a change the reader typed ─────
//
// Sprint 13, stage 3 (David, 28/09/2026). A scenario is a list of rows, each a
// symbol and the quantity it would have: in units, or as a dollar value that
// is turned into units at the live price. Nothing here says which way the
// change goes · the reader states the end state, not a buy or a sell.
//
// The before and after are measured by the SAME functions as "חשיפות התיק"
// (utils/portfolioExposures.js), run twice. The two screens can therefore
// never disagree about a portfolio.
//
// The scenario lives on this device only (sessionStorage) and is never sent
// to the server. The only network calls are the price and profile of a
// symbol that is not already in the portfolio, through the Finnhub proxy.

import { fetchQuote, fetchProfile } from "./finnhub";
import { industryToSector, readProfile, SECTOR_ETF, SECTOR_UNKNOWN } from "./sectorClassify";
import { valuedHoldings, concentration, sectors, currencies, regions, gaps, SECTOR_PENDING } from "./portfolioExposures";

// ── Sector of a symbol · moved from WhatIfSimulator.jsx ─────────────────────
// A live answer outranks the portfolio's tag; "אחר" is returned only when a
// lookup actually came back and matched no bucket. A failed lookup is
// "failed", an unanswered one "pending" · neither becomes "אחר".
export function resolveSector(holdingSector, liveIndustry, profileKind = null) {
  if (profileKind === "ERROR") return { sector: null, status: "failed" };
  if (profileKind === "ETF")   return { sector: SECTOR_ETF, status: "ok" };
  const live = industryToSector(liveIndustry || "");
  if (live) return { sector: live, status: "ok" };
  if (holdingSector) return { sector: holdingSector, status: "ok" };
  if (profileKind === "COMPANY" || profileKind === "UNRESOLVED") return { sector: SECTOR_UNKNOWN, status: "ok" };
  return { sector: null, status: "pending" };
}

// ── A symbol that is not in the portfolio ───────────────────────────────────
// Its price and sector from Finnhub. A symbol Finnhub cannot price (the free
// plan covers US listings only, CLAUDE.md) is answered { error: "no_price" }
// and stays out of the calculation.
export const LOOKUP_MS = 10000;
export async function lookupSymbol(symbol) {
  const s = String(symbol || "").trim().toUpperCase();
  if (!s) return { error: "empty" };
  try {
    // At most LOOKUP_MS: an answer that never comes is a failure ("נסה שוב"),
    // not a row that waits · and holds the unit switch · for good (review).
    const timeout = new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), LOOKUP_MS));
    const [quote, profile] = await Promise.race([Promise.all([fetchQuote(s), fetchProfile(s)]), timeout]);
    const price = Number(quote?.c);
    if (!(price > 0)) return { symbol: s, error: "no_price" };
    const { sector } = resolveSector(null, profile?.finnhubIndustry, readProfile(profile));
    return {
      symbol: s,
      name: profile?.name || s,
      price,
      quoteAt: Number(quote?.t) > 0 ? Number(quote.t) * 1000 : null,
      sector,
    };
  } catch {
    return { symbol: s, error: "failed" };
  }
}

// ── The rows ─────────────────────────────────────────────────────────────────
// { id, symbol, unit: "shares" | "usd", value: string }
// `value` is what the reader typed; "" means the row changes nothing yet.
let seq = 0;
export const newRow = (symbol = "", value = "") => ({ id: `r${Date.now().toString(36)}${(seq++).toString(36)}`, symbol, unit: "shares", value });

// The quantity a row sets, in units, or null when the row sets nothing.
export function targetShares(row, price) {
  const n = Number(String(row.value).replace(/,/g, ""));
  if (String(row.value).trim() === "" || !Number.isFinite(n) || n < 0) return null;
  if (row.unit === "usd") return price > 0 ? n / price : null;
  return n;
}

// The portfolio as it would be. A held symbol takes the quantity its row
// states; a new symbol is added at its live price; 0 takes it out. A row
// without a price for its symbol, or without a value, changes nothing.
// When two rows name the same symbol, the last one counts.
export function applyScenario(portfolio = [], rows = [], lookups = {}) {
  const after = portfolio.map(p => ({ ...p }));
  for (const row of rows) {
    const sym = String(row.symbol || "").trim().toUpperCase();
    if (!sym) continue;
    const idx = after.findIndex(p => p.symbol === sym);
    const held = idx >= 0 ? after[idx] : null;
    const heldPriced = held && held.quote !== "none" && held.currentPrice > 0;
    const look = lookups[sym];
    const price = heldPriced ? held.currentPrice : look && !look.error ? look.price : null;
    if (!price) continue;
    const shares = targetShares(row, price);
    if (shares == null) continue;
    if (held) after[idx] = { ...held, shares, currentPrice: price, quote: held.quote === "none" ? "ok" : held.quote };
    else after.push({ symbol: sym, name: look.name, shares, currentPrice: price, quote: "ok", quoteAt: look.quoteAt, sector: look.sector, scenario: true });
  }
  return after;
}

// ── Before and after, section by section ────────────────────────────────────
// Every section of "חשיפות התיק", as lines of { key, label, before, after,
// kind }. `kind` is "pct" (a share of the portfolio), "usd" or "count". A
// line absent on one side is 0 there, so a holding that leaves the portfolio
// reads as "x% → 0%". `changed` is false when the two sides round to the same
// figure on screen.
const EPS = { pct: 0.05, usd: 0.5, count: 0 };
const line = (key, label, before, after, kind = "pct", extra = {}) => ({
  key, label, before, after, kind,
  changed: Math.abs((after || 0) - (before || 0)) > EPS[kind],
  ...extra,
});

export function measure(portfolio, segmentsBySymbol) {
  const base = valuedHoldings(portfolio);
  const reg = segmentsBySymbol ? regions(base, segmentsBySymbol) : null;
  return {
    base,
    conc: concentration(base),
    sect: sectors(base),
    curr: currencies(base),
    reg,
    gaps: gaps(base, reg),
  };
}

export function compare(before, after) {
  const value = [line("total", "שווי התיק", before.base.total, after.base.total, "usd")];

  // 1 · Concentration: the largest holding, the top five, the count, and the
  // weight of every holding on either side.
  const top = m => m.conc.rows[0] || null;
  const conc = [
    line("count", "מספר ההחזקות", before.conc.count, after.conc.count, "count"),
    line("top5", "5 ההחזקות הגדולות", before.conc.topPct, after.conc.topPct),
    line("largest", "ההחזקה הגדולה ביותר", top(before)?.weightPct || 0, top(after)?.weightPct || 0, "pct",
      { beforeTag: top(before)?.symbol || null, afterTag: top(after)?.symbol || null,
        changed: (top(before)?.symbol !== top(after)?.symbol) || Math.abs((top(after)?.weightPct || 0) - (top(before)?.weightPct || 0)) > EPS.pct }),
  ];
  const weights = new Map();
  for (const r of before.conc.rows) weights.set(r.symbol, { b: r.weightPct, a: 0 });
  for (const r of after.conc.rows) weights.set(r.symbol, { ...(weights.get(r.symbol) || { b: 0 }), a: r.weightPct });
  const holdings = [...weights.entries()]
    .sort((x, y) => Math.max(y[1].a, y[1].b) - Math.max(x[1].a, x[1].b))
    .map(([sym, w]) => line(`w-${sym}`, sym, w.b, w.a, "pct", { ltr: true }));

  // 2 · Sectors.
  const sect = new Map();
  for (const s of before.sect) sect.set(s.sector, { b: s.weightPct, a: 0 });
  for (const s of after.sect) sect.set(s.sector, { ...(sect.get(s.sector) || { b: 0 }), a: s.weightPct });
  const tail = s => (s === SECTOR_PENDING ? 2 : s === SECTOR_UNKNOWN ? 1 : 0);
  const sectorLines = [...sect.entries()]
    .sort((x, y) => (tail(x[0]) - tail(y[0])) || (Math.max(y[1].a, y[1].b) - Math.max(x[1].a, x[1].b)))
    .map(([s, w]) => line(`s-${s}`, s, w.b, w.a));

  // 3 · Currency.
  const cur = new Map();
  for (const c of before.curr) cur.set(c.code, { b: c.weightPct, a: 0 });
  for (const c of after.curr) cur.set(c.code, { ...(cur.get(c.code) || { b: 0 }), a: c.weightPct });
  const currencyLines = [...cur.entries()].map(([code, w]) => line(`c-${code}`, code, w.b, w.a, "pct", { ltr: true }));

  // 4 · Revenue by region, and the part not broken out.
  let regionLines = null;
  if (before.reg && after.reg) {
    const reg = new Map();
    for (const p of before.reg.places) reg.set(p.id, { b: p.pct, a: 0 });
    for (const p of after.reg.places) reg.set(p.id, { ...(reg.get(p.id) || { b: 0 }), a: p.pct });
    regionLines = [...reg.entries()]
      .filter(([, w]) => w.a > 0 || w.b > 0)
      .sort((x, y) => Math.max(y[1].a, y[1].b) - Math.max(x[1].a, x[1].b))
      .map(([id, w]) => line(`g-${id}`, id, w.b, w.a, "pct", { region: true }));
    if (before.reg.notBrokenOutPct > 0 || after.reg.notBrokenOutPct > 0) {
      regionLines.push(line("g-none", "לא פורט לאזור", before.reg.notBrokenOutPct, after.reg.notBrokenOutPct, "pct", { muted: true }));
    }
  }

  // 5 · What we do not know · the holdings with a gap, on each side.
  const gapSyms = m => new Set(m.gaps.map(g => g.symbol));
  const gb = gapSyms(before), ga = gapSyms(after);
  const gapsAdded = after.gaps.filter(g => !gb.has(g.symbol));
  const gapsRemoved = before.gaps.filter(g => !ga.has(g.symbol));

  return { value, conc, holdings, sectors: sectorLines, currency: currencyLines, regions: regionLines, gapsAdded, gapsRemoved, gapsAfter: after.gaps };
}

// ── The scenario as the reader sees it (sprint 14, stage 1) ─────────────────
// The screen opens on the portfolio itself: one row per holding at today's
// quantity, and a blank row for another symbol. What is kept is only what
// differs from today:
//   { v: 2, unit: "shares" | "usd", overrides: { SYM: "typed" }, extras: [{ id, symbol, value }] }
// One unit for the whole screen (David, 06/10/2026). Today's portfolio is not
// stored · it is read from the app each time, so a holding bought or sold
// since shows as it is now, and an override on a symbol no longer held is
// dropped.
export const EMPTY_STATE = { v: 2, unit: "shares", overrides: {}, extras: [] };

const priceOf = h => (h && h.quote !== "none" && h.currentPrice > 0 ? h.currentPrice : null);

// Holdings in the order of "חשיפות התיק": largest first, the ones without a
// price last.
export function heldOrder(portfolio = []) {
  const held = portfolio.filter(p => Number(p.shares) > 0);
  const value = p => (priceOf(p) ? p.currentPrice * p.shares : -1);
  return [...held].sort((a, b) => value(b) - value(a));
}

// Today's figure for a holding, in the screen's unit, as the field shows it.
const trim = n => String(Math.round(n * 10000) / 10000);
export function todayValue(holding, unit) {
  if (unit === "usd") { const p = priceOf(holding); return p ? String(Math.round(holding.shares * p)) : ""; }
  return trim(Number(holding.shares));
}

// What the field shows for a holding: what was typed, or today's figure.
export function heldValue(state, holding) {
  const o = state.overrides[holding.symbol];
  return o !== undefined ? o : todayValue(holding, state.unit);
}

// Whether a holding's row says something other than today. A field cleared
// to nothing changes nothing.
export function heldChanged(state, holding) {
  const o = state.overrides[holding.symbol];
  if (o === undefined || String(o).trim() === "") return false;
  const p = priceOf(holding);
  const shares = targetShares({ unit: state.unit, value: o }, p);
  if (shares == null) return false;
  // In dollars, today's figure is rounded to the dollar: a value equal to it
  // is today's, not a change of a fraction of a share.
  if (state.unit === "usd") return String(Math.round(Number(String(o).replace(/,/g, "")))) !== todayValue(holding, "usd");
  return Math.abs(shares - holding.shares) > 1e-9;
}

// The rows applyScenario reads: every changed holding, and every extra row
// with a symbol and a value.
export function scenarioRows(portfolio, state) {
  const rows = [];
  for (const h of heldOrder(portfolio)) {
    if (heldChanged(state, h)) rows.push({ id: `h-${h.symbol}`, symbol: h.symbol, unit: state.unit, value: state.overrides[h.symbol] });
  }
  // An extra row naming a held symbol is not counted: that holding's own row
  // sets its quantity, and the screen says so on the extra row.
  const heldSet = new Set(portfolio.filter(p => Number(p.shares) > 0).map(p => p.symbol));
  for (const x of state.extras) {
    const sym = String(x.symbol || "").trim().toUpperCase();
    if (sym && !heldSet.has(sym) && String(x.value || "").trim() !== "") rows.push({ ...x, unit: state.unit });
  }
  return rows;
}

// Switching the unit converts what was typed, at the price on screen, so a
// change the reader made survives the switch. A value with no price for its
// symbol is cleared rather than reinterpreted.
export function convertValue(value, from, to, price) {
  if (from === to || String(value).trim() === "") return value;
  const n = Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(n) || !(price > 0)) return "";
  // Dollars keep their cents: rounding to the dollar made a round trip drift
  // (15 units at $33.33 → $500 → 15.0015 units · review).
  return to === "usd" ? String(Math.round(n * price * 100) / 100) : trim(n / price);
}

// The extra rows whose price is still on its way: a symbol that is not held,
// a value, and no answer yet. The screen holds the unit switch until the
// answer comes, rather than clearing what was typed. A symbol the source
// answered without a price (or that failed) does not hold it: that row is out
// of the calculation, and its value is cleared by the switch (review).
export function unconvertible(state, lookups = {}, portfolio = []) {
  const held = new Set(portfolio.filter(p => Number(p.shares) > 0).map(p => p.symbol));
  return state.extras.filter(x => {
    const sym = String(x.symbol || "").trim().toUpperCase();
    return sym && !held.has(sym) && String(x.value || "").trim() !== "" && !(sym in lookups);
  }).map(x => String(x.symbol).trim().toUpperCase());
}

export function switchUnit(state, unit, portfolio, lookups = {}) {
  if (unit === state.unit) return state;
  // A holding whose field still reads today's figure (within the rounding
  // the screen shows) goes back to today in the new unit, rather than
  // carrying a fraction of a share across (review).
  const overrides = {};
  for (const [sym, v] of Object.entries(state.overrides)) {
    const h = portfolio.find(p => p.symbol === sym);
    if (h && heldChanged(state, h)) overrides[sym] = convertValue(v, state.unit, unit, priceOf(h));
  }
  const extras = state.extras.map(x => {
    const l = lookups[String(x.symbol || "").trim().toUpperCase()];
    const price = l && !l.error ? l.price : null;
    // No price, no conversion: the value is cleared rather than read in the
    // other unit once a price arrives ("נסה שוב" after a failure · review).
    return { ...x, value: price ? convertValue(x.value, state.unit, unit, price) : "" };
  });
  return { ...state, unit, overrides, extras };
}

export function isPristine(state, portfolio) {
  return !heldOrder(portfolio).some(h => heldChanged(state, h))
    && !state.extras.some(x => String(x.symbol || "").trim() || String(x.value || "").trim());
}

// ── Where the scenario is kept ──────────────────────────────────────────────
// This device, this tab session. Every read and write guarded: a private
// window or blocked storage leaves the screen working, with nothing kept.
// "si_scenario_v1" (sprint 13, a list of free rows) is not read: it lived for
// one tab session only, and there is nothing in it to carry over.
const KEY = "si_scenario_v2";
export function readState() {
  try {
    const v = JSON.parse(sessionStorage.getItem(KEY) || "null");
    if (!v || v.v !== 2) return null;
    const overrides = {};
    for (const [k, val] of Object.entries(v.overrides || {})) if (typeof k === "string") overrides[k] = String(val ?? "");
    const seen = new Set();
    const extras = (Array.isArray(v.extras) ? v.extras : [])
      .filter(r => r && typeof r.id === "string" && !seen.has(r.id) && seen.add(r.id))
      .map(r => ({ id: r.id, symbol: String(r.symbol || ""), value: String(r.value ?? "") }));
    return { v: 2, unit: v.unit === "usd" ? "usd" : "shares", overrides, extras };
  } catch { return null; }
}
export function writeState(state) {
  try { sessionStorage.setItem(KEY, JSON.stringify(state)); } catch {}
}

// ── Opening the screen on a company (the company page's "בדוק בתרחיש") ──────
// The symbol waits here until the screen takes it, the same way a lesson
// waits for the Academy (utils/academy.js). A held symbol's row is
// highlighted and focused; another symbol goes into the blank row, with no
// quantity · no direction, no amount.
let pending = null;
export function openScenarioWith(symbol) {
  pending = String(symbol || "").trim().toUpperCase() || null;
  window.dispatchEvent(new CustomEvent("si-open-scenario", { detail: { symbol: pending } }));
}
export function takePendingSymbol() {
  const s = pending;
  pending = null;
  return s;
}
